import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
// No `mapbox-gl` import: the only value use (new mapboxgl.Popup) was removed
// with the popup. The remaining `mapboxgl.Map` type annotations resolve via
// mapbox-gl's UMD global namespace (export as namespace mapboxgl).
import {
  Navigation, X, AlertTriangle,
  LocateFixed, Loader2, CreditCard,
} from 'lucide-react';
import { useTheme } from '@evi/skins';
import { fetchPublicLocations, type PublicLocation, type PublicLocationDetailCharger } from '@/lib/api';
import { QuickActions } from '@/components/QuickActions';
import { AnnouncementCard } from '@/components/announcements/AnnouncementCard';
import { useAuthStore } from '@/store/auth';
import { useCurrentMode } from '@/hooks/useCurrentMode';
import { PwaMapContainer } from '@/components/map/PwaMapContainer';
import { PwaMapMarkers } from '@/components/map/PwaMapMarkers';
import { StallList } from '@/components/station/StallList';
import { FavoriteHeart } from '@/components/station/FavoriteHeart';
import { SHEET_REFRESH_MS, latestById } from '@/lib/mapSelection';
import { useStreamAwareInterval } from '@/store/sessionStream';
import {
  LOCATION_ZOOM, deviceLocale, deviceTimeZone, positionCenter, regionStartCamera,
  requestStartPosition, validCamera, writeGeoMemory,
  type LngLat, type MapCamera, type StartCamera,
} from '@/lib/mapStartCamera';
import { useTermsGateState } from '@/components/TermsGate';
import styles from './MapPage.module.css';

/** localStorage, or null where reading it throws (private mode, blocked). */
function safeLocalStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

// ═════════════════════════════════════════════════════════════════════
// Defensive coordinate normalization.
//
// A location was observed in the DB with `lng = -7987638282685921` —
// which is "-79.87638282685921" with the decimal point silently
// stripped by some upstream write bug (not yet root-caused). Supercluster
// culls points outside its ±180/±85 world bbox, so such a point
// never reaches the marker render path.
//
// Rather than staking the driver-facing map on every write path
// being bug-free, we normalize on read: if a coordinate is out of
// the valid WGS-84 range, divide by 10 until it fits. Log when we
// do so — the admin should fix the underlying record, but the
// driver still sees the station in the meantime.
// ═════════════════════════════════════════════════════════════════════
function normalizeLatLng(
  raw: { lat?: number; lng?: number } | undefined,
  contextName: string,
): { lat: number; lng: number } | null {
  if (!raw || typeof raw.lat !== 'number' || typeof raw.lng !== 'number'
    || !isFinite(raw.lat) || !isFinite(raw.lng)) {
    console.warn(`[MapPage] Ignoring missing or non-finite coordinates for ${contextName}`, raw);
    return null;
  }

  let { lat, lng } = raw;
  let fixed = false;

  // Bring lng into ±180 by decimal-shifting.
  let lngGuard = 0;
  while (Math.abs(lng) > 180 && lngGuard < 18) {
    lng /= 10;
    lngGuard += 1;
    fixed = true;
  }
  // Same for lat into ±90.
  let latGuard = 0;
  while (Math.abs(lat) > 90 && latGuard < 18) {
    lat /= 10;
    latGuard += 1;
    fixed = true;
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    console.warn(`[MapPage] Ignoring out-of-range coordinates for ${contextName}`, raw);
    return null;
  }

  if (fixed) {
    // eslint-disable-next-line no-console
    console.warn(
      `[MapPage] Recovered mangled coordinates for ${contextName}`,
      { original: raw, normalized: { lat, lng } },
    );
  }
  return { lat, lng };
}

// MAPBOX_TOKEN gates the map render; PwaMapContainer has its own
// missing-token fallback, but we surface a top-level fullscreen
// message via <NoApiKey> when it's absent so the rest of the page
// chrome (search bar, onboarding card, QuickActions) doesn't render
// over a useless map.
const MAPBOX_TOKEN = import.meta.env['VITE_MAPBOX_TOKEN'] as string | undefined;

interface LocationWithChargers extends PublicLocation {
  chargers: PublicLocationDetailCharger[];
}

/**
 * Driver-facing location-level aggregate status.
 *
 * Drives the map pin's tristate visual. Built by walking every
 * connector across every online charger at the location, filtering
 * out connectors in non-functional states (Faulted / Unavailable),
 * and counting "available for use" vs "actively in use." The split:
 *
 *   - 'all-available' : at least one Available connector, zero
 *                       active. Green dot, no overlay.
 *   - 'partial-active': both Available AND active counts non-zero.
 *                       Green dot + blue ring overlay (always-on).
 *   - 'fully-occupied': zero Available, at least one active. Blue
 *                       solid dot.
 *   - 'maintenance'   : any charger at the location is in an
 *                       active maintenance window. Beats every
 *                       occupancy state — drivers should steer away.
 *   - 'offline'       : every charger at the location is offline,
 *                       OR every usable connector is Faulted /
 *                       Unavailable. Muted gray dot.
 *
 * "Active" includes Charging, Finishing, SuspendedEV, SuspendedEVSE,
 * Preparing, Reserved — anything that means "another driver has
 * claimed this stall, you can't pick it." Matches the OCPP set
 * surfaced on the station-detail connector list (commit 9357316);
 * the only states that DON'T count as active are Available
 * (selectable) and Faulted/Unavailable (excluded from counts entirely
 * because they aren't usable by anyone). Maintenance is detected at
 * the charger level (maintenance_windows), not the connector level.
 *
 * Offline-charger connectors don't contribute to either count.
 * Mirrors the is_online-wins-over-connector-status pattern from
 * 9357316: a connector's last-known status is stale once its
 * charger's WebSocket has dropped, and drivers shouldn't be able
 * to start sessions through a disconnected charger regardless of
 * what the server's last-known state says.
 */
type DominantStatus =
  | 'all-available'
  | 'partial-active'
  | 'fully-occupied'
  | 'maintenance'
  | 'offline';

const ACTIVE_CONNECTOR_STATUSES = new Set<string>([
  'Charging',
  'Finishing',
  'SuspendedEV',
  'SuspendedEVSE',
  'Preparing',
  'Reserved',
]);
const UNUSABLE_CONNECTOR_STATUSES = new Set<string>([
  'Faulted',
  'Unavailable',
]);

function getDominantStatus(loc: LocationWithChargers): DominantStatus {
  const now = new Date();
  const inMaint = loc.chargers.some((c) =>
    (c.maintenance_windows ?? []).some((w) => new Date(w.start) <= now && now <= new Date(w.end))
  );
  if (inMaint) return 'maintenance';

  const onlineChargers = loc.chargers.filter((c) => c.is_online);
  if (onlineChargers.length === 0) return 'offline';

  // Walk every connector on every online charger, drop the
  // unusable ones (Faulted / Unavailable contribute to neither
  // count). The remaining pool is what drivers might consider
  // for picking a stall.
  const usable = onlineChargers
    .flatMap((c) => c.connectors)
    .filter((conn) => !UNUSABLE_CONNECTOR_STATUSES.has(conn.status));
  if (usable.length === 0) return 'offline';

  const availableCount = usable.filter((conn) => conn.status === 'Available').length;
  const activeCount = usable.filter((conn) => ACTIVE_CONNECTOR_STATUSES.has(conn.status)).length;

  if (availableCount > 0 && activeCount === 0) return 'all-available';
  if (availableCount > 0 && activeCount > 0) return 'partial-active';
  if (availableCount === 0 && activeCount > 0) return 'fully-occupied';
  // Defensive: usable pool exists but neither Available nor active —
  // means every remaining connector is in some other state we don't
  // model here. Treat as offline since the driver can't act on any
  // of them right now.
  return 'offline';
}

const STATUS_COLOR: Record<DominantStatus, string> = {
  'all-available':  '#2ECC71',
  'partial-active': '#2ECC71', // green dot; the blue ring is signaled separately
  'fully-occupied': '#3498db',
  maintenance:      '#f39c12',
  offline:          '#777777',
};

// Status labels are now resolved via t(`status.${status}`) at the render site
// (i18n) rather than a module-scope map.

// Per-state connector breakdown for the popup badges. Mirrors the
// station-detail mapping (one source of truth for the driver-facing buckets):
// offline-charger connectors count as Unavailable (is_online wins); 'Occupied'
// is in-use (Charging); any unknown/future status falls into Unavailable
// defensively. Colors are the same five-bucket palette the station-detail dots
// use, so the two surfaces read consistently.
type ConnectorBucketKey = 'available' | 'preparing' | 'charging' | 'pluggedIn' | 'unavailable';

const CONNECTOR_BUCKETS: ReadonlyArray<{ key: ConnectorBucketKey; label: string; color: string }> = [
  { key: 'available',   label: 'Available',   color: '#16A34A' },
  { key: 'preparing',   label: 'Preparing',   color: '#3498db' },
  { key: 'charging',    label: 'Charging',    color: '#F59E0B' },
  { key: 'pluggedIn',   label: 'Plugged in',  color: '#64748b' },
  { key: 'unavailable', label: 'Unavailable', color: '#DC2626' },
];

function connectorBuckets(loc: LocationWithChargers): Record<ConnectorBucketKey, number> {
  const b: Record<ConnectorBucketKey, number> = {
    available: 0, preparing: 0, charging: 0, pluggedIn: 0, unavailable: 0,
  };
  for (const c of loc.chargers) {
    for (const conn of c.connectors ?? []) {
      if (!c.is_online) { b.unavailable += 1; continue; }
      switch (conn.status) {
        case 'Available': b.available += 1; break;
        case 'Preparing': b.preparing += 1; break;
        case 'Charging':
        case 'SuspendedEVSE':
        case 'Occupied': b.charging += 1; break;
        case 'SuspendedEV':
        case 'Finishing': b.pluggedIn += 1; break;
        case 'Unavailable':
        case 'Faulted':
        case 'Reserved': b.unavailable += 1; break;
        default: b.unavailable += 1; break;
      }
    }
  }
  return b;
}

// dotPresentation lives in @/components/map/PwaMapMarkers now — the
// marker layer is the only consumer. Removed from MapPage in Phase 2.

interface Props { onNavigate: (path: string) => void }

export function MapPage({ onNavigate }: Props) {
  if (!MAPBOX_TOKEN) {
    return <NoApiKey />;
  }
  return <MapInner onNavigate={onNavigate} />;
}

/**
 * Payment-method onboarding card (Layer 1 of the driver-friction fix).
 *
 * Renders only when the auth store's user.has_payment_method is
 * false — i.e. the driver hasn't added a Stripe payment method yet.
 * Dismissible per-tab-session via sessionStorage; the card returns
 * on the next session until a card is actually on file. The
 * `has_payment_method` flag is server-derived; PaymentMethodsPage
 * calls useAuthStore.refreshUser() on add/setDefault/delete which
 * flips the flag, the card unmounts, and dismiss state becomes
 * irrelevant.
 *
 * Why sessionStorage (not localStorage):
 * a localStorage dismiss would persist past the spec's "reappears
 * next session until a card is on file" requirement. sessionStorage
 * dies with the tab; the next visit shows the card again.
 */
const ONBOARDING_DISMISS_KEY = 'evi.payment-onboarding.dismissed';

function PaymentMethodOnboardingCard({
  onNavigate,
}: {
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation('map');
  const user = useAuthStore((s) => s.user);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(ONBOARDING_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Show only when:
  //   - user is loaded (avoids flash on first paint)
  //   - has_funding_source is explicitly false (undefined = a
  //     login-style payload, which does not carry the field, or a
  //     legacy persisted user; we treat as "we don't know" and skip
  //     the card rather than show a false positive — the auth store
  //     will refresh next time the user reaches PaymentMethodsPage or
  //     the login flow re-runs)
  //
  // has_funding_source, NOT has_payment_method: this card asks the
  // driver to add a card, and a driver whose wallet already covers
  // their own tenant's minimum does not need one. Nagging them anyway
  // is what the v1 mirror would have done to every wallet-funded
  // driver it delivered, forever — the card returns each tab session
  // "until a card is actually on file", and for them that is never.
  if (!user) return null;
  if (user.has_funding_source !== false) return null;
  if (dismissed) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(ONBOARDING_DISMISS_KEY, '1');
    } catch {
      // Private mode or quota — accept the in-memory dismiss only;
      // the card will reappear on next page load, which matches the
      // spec better than persisting via a fallback.
    }
    setDismissed(true);
  };

  return (
    <div className={styles.onboardingCard} role="status">
      <div className={styles.onboardingHeader}>
        <CreditCard size={18} className={styles.onboardingIcon} />
        <h2 className={styles.onboardingTitle}>{t('onboarding.title')}</h2>
        <button
          type="button"
          className={styles.onboardingDismiss}
          onClick={dismiss}
          aria-label={t('onboarding.dismiss')}
        >
          <X size={16} />
        </button>
      </div>
      <p className={styles.onboardingBody}>
        {t('onboarding.body')}
      </p>
      <button
        type="button"
        className={styles.onboardingCta}
        onClick={() => onNavigate('/billing/payment-methods')}
      >
        <CreditCard size={14} /> {t('onboarding.cta')}
      </button>
    </div>
  );
}

function NoApiKey() {
  const { t } = useTranslation('map');
  return (
    <div className={styles.fullscreen}>
      <div className={styles.errorCard}>
        <AlertTriangle size={32} style={{ color: 'var(--color-warn)', marginBottom: 12 }} />
        <h2 style={{ margin: '0 0 8px', color: 'var(--color-tx)', fontSize: 18 }}>{t('noApiKey.title')}</h2>
        <p style={{ margin: 0, color: 'var(--color-tx-m)', fontSize: 14, textAlign: 'center' }}>
          Set <code>VITE_MAPBOX_TOKEN</code> in <code>apps/web/.env</code> to display the map.
        </p>
      </div>
    </div>
  );
}

function MapInner({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useTranslation('map');
  // Phase 9 — driver state for the per-marker visual treatment.
  // useCurrentMode reads from the same auth-store singleton the
  // Phase 3 toggle uses, so a mode flip from the top-nav pill or
  // Account sheet propagates to every marker on the same render.
  const user = useAuthStore((s) => s.user);
  const { mode: currentMode } = useCurrentMode();
  const [selected, setSelected] = useState<LocationWithChargers | null>(null);
  useEffect(() => { setSelected(null); }, [currentMode, user?._id, user?.fleet_id]);
  // searchQuery + filtersOpen state both retired — Phase 3 Mapbox
  // Geocoding is deferred indefinitely (drivers tap location beacons
  // rather than search by address), and the filter toggle that
  // opened the status legend was removed with the rest of the bar
  // since its only purpose was driving that single panel.
  // Mirror the active theme's mode onto the Mapbox style URL so the
  // map tiles flip light/dark alongside the app chrome.
  const { mode, theme } = useTheme();
  // Opening camera (Warren, item 11, 2026-10-23; rules in
  // lib/mapStartCamera.ts). First paint: the driver's region from the
  // phone's time zone, else their country from its language, else the
  // North America pose ([-98.5, 40] at zoom 2.2, tuned off the admin
  // dashboard's OVERVIEW_CAMERA, with pitch 6 / bearing 0 passed to
  // PwaMapContainer below; lower pitch raises the globe up the frame).
  // The driver's own position replaces it as soon as the browser gives
  // it (see the location effects after `map`). Lng-first, as mapbox-gl
  // wants.
  const startCameraRef = useRef<StartCamera | null>(null);
  if (!startCameraRef.current) {
    startCameraRef.current = regionStartCamera(deviceTimeZone(), deviceLocale());
  }
  // Survives PwaMapContainer remounts (the key={...} flips on a theme or
  // mode change force a constructor-level rebuild because mapbox-gl's
  // `style` is set at construction). onCameraChange writes every settled
  // camera here, after checking it, so the next mount restores the
  // driver's view rather than the opening pose.
  const savedCameraRef = useRef<MapCamera>({
    center: startCameraRef.current.center,
    zoom: startCameraRef.current.zoom,
  });

  // Live mapbox-gl instance — handed up by PwaMapContainer once the
  // map mounts, cleared on unmount. Sibling components (markers,
  // popup, selection-camera, locate FAB) gate their imperative
  // side-effects on this being non-null.
  const [map, setMap] = useState<mapboxgl.Map | null>(null);

  // ── The driver's position for the opening camera ─────────────────────
  // Asked once per map visit, and only once the terms gate is out of the
  // way: a permission prompt stacked on the terms card is the wrong moment,
  // and the first-run gate is exactly where a new driver meets the map.
  // requestStartPosition applies the ask policy (granted: always; denied:
  // never; prompt: until the driver says no). The position stays in this
  // component: it is never stored or sent.
  const termsGate = useTermsGateState();
  const termsClear = termsGate.settled && !termsGate.open;
  const [driverCenter, setDriverCenter] = useState<LngLat | null>(null);
  const askedRef = useRef(false);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);
  useEffect(() => {
    if (!termsClear || askedRef.current) return;
    askedRef.current = true;
    void requestStartPosition({
      geolocation: typeof navigator === 'undefined' ? null : navigator.geolocation,
      permissions: typeof navigator === 'undefined' ? null : navigator.permissions,
      storage: safeLocalStorage(),
    }).then((r) => {
      if (aliveRef.current && r.center) setDriverCenter(r.center);
    });
  }, [termsClear]);

  // The camera belongs to the driver once they touch it: a pan, pinch,
  // tap on a location or the locate button means a fix that arrives later
  // must not yank the view away. Programmatic eases carry no originalEvent.
  const userMovedRef = useRef(false);
  useEffect(() => {
    if (!map) return;
    // mapbox types leave originalEvent off zoomstart/pitchstart; it is
    // there at runtime whenever a gesture (not a camera call) fired it.
    const mark = (e: object) => {
      if ((e as { originalEvent?: unknown }).originalEvent) userMovedRef.current = true;
    };
    map.on('dragstart', mark);
    map.on('zoomstart', mark);
    map.on('rotatestart', mark);
    map.on('pitchstart', mark);
    return () => {
      map.off('dragstart', mark);
      map.off('zoomstart', mark);
      map.off('rotatestart', mark);
      map.off('pitchstart', mark);
    };
  }, [map]);
  useEffect(() => {
    if (selected) userMovedRef.current = true;
  }, [selected]);

  // Centre on the driver when the fix lands, whether that is before the
  // map exists or long after first paint (a late answer to the prompt, a
  // slow GPS). Once per visit; the easeTo's moveend saves the camera.
  const recenteredRef = useRef(false);
  useEffect(() => {
    if (!map || !driverCenter || recenteredRef.current) return;
    recenteredRef.current = true;
    if (userMovedRef.current) return;
    map.easeTo({ center: driverCenter, zoom: LOCATION_ZOOM, duration: 900 });
  }, [map, driverCenter]);

  // Fleet Mode map-visibility extension — `currentMode` is part of
  // both the query key AND the queryFn argument. The cache key change
  // is what triggers a refetch when the driver toggles fleet ↔ public:
  // React Query treats `['public-locations', 'fleet']` and
  // `['public-locations', 'public']` as separate queries, so a flip
  // fires a fresh fetch with the new mode (server widens the
  // cross-tenant visibility branch on public). Without the key, the
  // map stayed showing whatever the first-paint mode returned —
  // typically the fleet driver's narrow own-tenant set even after
  // they toggled to public. See useApplyTheme parallel for the
  // "include the param in the key, not just the queryFn" pattern.
  //
  // The sheet's Start buttons read this list, so while a location is
  // selected it refreshes: once on the tap (effect below) and every
  // SHEET_REFRESH_MS after, until the sheet closes. Nothing polls while
  // no sheet is open. Live cards (28 Oct 2026): charger events refetch
  // the list (lib/liveInvalidation.ts), so with the stream live the open
  // sheet does not poll either; SHEET_REFRESH_MS is the fallback.
  const sheetRefreshMs = useStreamAwareInterval(SHEET_REFRESH_MS);
  const locationsQ = useQuery({
    queryKey: ['public-locations', user?._id, user?.fleet_id, currentMode],
    queryFn: () => fetchPublicLocations(currentMode),
    refetchInterval: selected ? sheetRefreshMs : false,
  });
  const selectedId = selected?._id ?? null;
  const { refetch: refetchLocations } = locationsQ;
  useEffect(() => {
    if (selectedId) void refetchLocations();
  }, [selectedId, refetchLocations]);
  // chargersQ dropped (B-3 fix): the driver_map list now embeds
  // per-location filtered chargers. The previous separate
  // fetchPublicChargers() call was tenant-scoped and produced the
  // "1 chargers · 0 online" mismatch on cross-tenant locations
  // because the client-side join found zero matches.

  const locWithChargers: LocationWithChargers[] = useMemo(() => {
    const locs = locationsQ.data ?? [];
    // Server applies the visibility contract now (active + visible
    // on driver map + coords present) via `driver_map=1`. The client
    // filter is defense-in-depth — if the server ever returns a stray
    // row without coordinates we still don't crash on `.lat`/`.lng`
    // access downstream. Keep it narrow (coords only) so legitimate
    // server filtering doesn't get masked by client-side drops.
    //
    // Embedded `chargers` from the driver_map list (B-3 fix). Falls
    // back to [] for serializations predating the embed (defensive;
    // no real client should hit this path post-deploy).
    return locs
      .filter((l) => l.address.coordinates)
      .map((l) => ({ ...l, chargers: l.chargers ?? [] }));
  }, [locationsQ.data]);

  // isLoading dropped — was only consumed by the search bar
  // placeholder copy, which retired with the bar removal.

  // The sheet renders the latest copy of the selected location (found by
  // _id in the refreshed list), so a stall that went busy, offline or
  // into maintenance since the tap shows it and its Start follows. The
  // tap's snapshot stands in until the list has it. The camera keeps the
  // snapshot, so a refresh never re-eases the map.
  const sheetLoc = useMemo(() => latestById(locWithChargers, selected), [locWithChargers, selected]);

  return (
    <div className={styles.fullscreen}>
      {/* Only when there is nothing to show: a failed sheet refresh keeps
          the last list on screen rather than raising this banner. */}
      {locationsQ.isError && !locationsQ.data && <div role="alert" style={{ position: 'absolute', top: 80, left: 16, right: 16, zIndex: 10, padding: 16, background: 'var(--color-sf)', color: 'var(--color-tx)', borderRadius: 12 }}>
        {t('loadError', { defaultValue: 'Locations could not be loaded.' })}{' '}
        <button onClick={() => void locationsQ.refetch()}>{t('retry', { defaultValue: 'Retry' })}</button>
      </div>}
      {/* Phase 1 of the Google Maps -> Mapbox migration. Bare map
          only — no markers, no popups, no search, no geolocate.
          Phase 2 restores marker rendering (mapboxgl.Marker with
          EviMapDot DOM), Phase 3 wires Mapbox Geocoding into the
          search input below, Phase 5 handles popups + clustering +
          geolocate. The legacy ClusteredMarkers / InfoContent /
          CameraPersister / SelectionCamera / LocateMeButton function
          definitions still live in this file as scaffolding for
          Phase 2; they're NOT rendered here. Selecting a station
          via the existing BottomSheet code path also won't pan the
          map this commit (selection -> camera fly lands in Phase 2
          alongside the marker port).

          key forces a remount on theme + mode flip — mapbox-gl
          style is constructor-time, runtime swap is unreliable.
          theme.id is included because the Cartographic theme
          (and any future theme with its own mapStyle override)
          swaps the basemap URL on a theme flip; without it in
          the key, switching from Default → Cartographic at the
          same mode would not re-instantiate the map.
          Mirrors the admin pattern (DashboardMapBackground.tsx
          uses the same key={mode} on MapContainer). */}
      <PwaMapContainer
        key={`${theme.id}-${mode}`}
        mode={mode}
        initialCenter={savedCameraRef.current.center}
        initialZoom={savedCameraRef.current.zoom}
        // Tilted-globe framing. PITCH is the height lever: LOWER pitch
        // raises the globe up the frame (toward 0 = highest), higher
        // pitch tilts toward the horizon and drops it down. pitch 6,
        // bearing 0. The map's pitch/rotate gestures stay disabled, so
        // this is a fixed opening tilt the user pans/zooms within.
        initialPitch={6}
        initialBearing={0}
        onCameraChange={(camera) => {
          const checked = validCamera(camera, 'camera change');
          if (checked) savedCameraRef.current = checked;
        }}
        onMapReady={setMap}
      />

      {/* Imperative marker layer. Renders nothing in the React tree
          (returns null); manages mapboxgl.Marker instances + their
          embedded React roots in effects against `map`. */}
      {map && (
        <PwaMapMarkers
          map={map}
          locations={locWithChargers}
          selectedId={selected?._id ?? null}
          onSelect={setSelected}
          // Phase 9 — drives per-marker visual treatment based on
          // each location's accessibility under the driver's
          // current mode + fleet attribution. Mode flips through
          // the existing useCurrentMode hook (Phase 3) trigger a
          // marker re-render via the dependency array in the
          // marker effect.
          userMode={currentMode}
          userFleetId={user?.fleet_id ?? null}
        />
      )}

      {/* Camera animator — eases to the selected location at z14
          (matches Google-era SelectionCamera behaviour). */}
      {map && selected && (
        <MapboxSelectionCamera map={map} selected={selected} />
      )}

      {/* Single surface: the BottomSheet below is the only location card.
          The earlier mapboxgl.Popup (InfoContent) dual-surface was removed —
          it overlapped the sheet and stole map you could pinch on. */}

      {/* Locate-me FAB — browser navigator.geolocation, then map.easeTo. */}
      {map && <LocateMeButton map={map} onLocate={() => { userMovedRef.current = true; }} />}

      {/* Search bar + filter toggle + status legend all dropped this
          commit. Phase 3 Mapbox Geocoding is deferred indefinitely
          (drivers tap beacons not search by address), and the filter
          toggle's only job was opening the legend panel — which was
          a soft "what do the dot colours mean" reference rather than
          a critical affordance. With no toggle reachable, the legend
          becomes orphan UI, so both come out together. STATUS_COLOR
          / STATUS_LABEL constants stay (InfoContent's inline status
          pill still consumes them). Orphan CSS rules (.searchBar,
          .filterBtn, .legend, .legendOpen, .legendDot, .legendItem,
          .legendTitle, .searchInput, .searchIcon) left in the module
          for now — sweeping unused CSS is a separate cleanup pass.
          Filter toggle relocation to a FAB is a follow-up if product
          ever wants the legend back. */}

      {/* Payment-method onboarding card (driver-friction fix).
          Renders when the auth store says the driver hasn't added a
          card yet. Dismissible per-tab-session via sessionStorage;
          reappears next session until a card is actually on file. */}
      <PaymentMethodOnboardingCard onNavigate={onNavigate} />

      {/* Admin-published announcement — gated on `map` so the card
          only appears once the map is actually ready, never over a
          blank canvas. Yields to the guided tour and to payment
          onboarding (guards live inside the component); shares the
          onboarding card's anchor since only one renders at a time. */}
      {map && <AnnouncementCard onNavigate={onNavigate} />}

      {/* Bottom sheet: overview + stalls with Start, one step. */}
      {sheetLoc && (
        <BottomSheet
          loc={sheetLoc}
          onClose={() => setSelected(null)}
          onNavigate={(path) => {
            setSelected(null);
            onNavigate(path);
          }}
        />
      )}

      {/* Floating quick-actions — hidden while the station bottom sheet is open. */}
      {!selected && <QuickActions onNavigate={onNavigate} />}
    </div>
  );
}

/**
 * Eases the map camera to the selected location at z14 (close enough
 * to read street names, wide enough to keep block-level context).
 * Mirrors the Google-era SelectionCamera behaviour using mapbox-gl's
 * easeTo({ center, zoom }) — the easing duration is short enough that
 * the driver perceives the camera as "snapping to" the pin without
 * a jarring teleport.
 */
function MapboxSelectionCamera({
  map,
  selected,
}: {
  map: mapboxgl.Map;
  selected: LocationWithChargers;
}) {
  // Capture the camera at the moment of FIRST selection (mount) so
  // we can restore on full unmount (selected → null). Reads from the
  // map BEFORE the easeTo effect below runs because useEffect fires
  // in declaration order. Stored in a ref rather than state so
  // subsequent selection switches (loc A → loc B) don't reset it —
  // the component stays mounted across switches, only the second
  // effect re-runs.
  const preSelectCameraRef = useRef<{ center: [number, number]; zoom: number } | null>(null);
  useEffect(() => {
    const center = map.getCenter();
    preSelectCameraRef.current = {
      center: [center.lng, center.lat],
      zoom: map.getZoom(),
    };
    return () => {
      // Fires only on full unmount (selected goes truthy → null).
      // Switching between markers keeps this effect's deps [map]
      // stable, so the cleanup doesn't fire mid-switch — the
      // selected-change effect below handles those animations
      // directly via easeTo to the new marker.
      const saved = preSelectCameraRef.current;
      if (!saved) return;
      map.easeTo({
        center: saved.center,
        zoom: saved.zoom,
        duration: 500,
      });
    };
  }, [map]);

  useEffect(() => {
    const norm = normalizeLatLng(
      selected.address.coordinates,
      `selection ${selected._id}`,
    );
    if (!norm) return;
    map.easeTo({
      center: [norm.lng, norm.lat],
      zoom: 14,
      duration: 500,
    });
  }, [map, selected]);
  return null;
}

/**
 * Circular FAB that pans the map to the driver's current location.
 * Sits below the filter menu panel on the right edge. Uses the
 * browser's Geolocation API — first click may prompt for permission.
 * Camera move via mapbox-gl easeTo replaces the Google-era
 * map.panTo / map.setZoom pair.
 */
function LocateMeButton({ map, onLocate }: { map: mapboxgl.Map; onLocate?: () => void }) {
  const { t } = useTranslation('map');
  const [status, setStatus] = useState<'idle' | 'locating' | 'error'>('idle');

  const handleClick = () => {
    onLocate?.();
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('error');
      return;
    }
    setStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // The answer also sets whether opening the map asks next time
        // (lib/mapStartCamera.ts). Only the answer is kept, never the fix.
        writeGeoMemory(safeLocalStorage(), 'allowed');
        // Named fields, so never swapped; still checked like any centre.
        const center = positionCenter(pos.coords);
        if (!center) {
          setStatus('error');
          setTimeout(() => setStatus('idle'), 2_000);
          return;
        }
        map.easeTo({
          center,
          zoom: 14,
          duration: 500,
        });
        setStatus('idle');
      },
      (err) => {
        if (err?.code === 1) writeGeoMemory(safeLocalStorage(), 'declined');
        // Permission denied or position unavailable. Clear the
        // locating state after a beat so the user can retry.
        setStatus('error');
        setTimeout(() => setStatus('idle'), 2_000);
      },
      { enableHighAccuracy: false, timeout: 8_000, maximumAge: 60_000 },
    );
  };

  return (
    <button
      type="button"
      className={styles.locateBtn}
      onClick={handleClick}
      aria-label={t('locate.ariaLabel')}
      title={status === 'error' ? t('locate.titleError') : t('locate.titleDefault')}
      disabled={status === 'locating'}
    >
      {status === 'locating'
        ? <Loader2 size={18} className={styles.locateSpin} />
        : <LocateFixed size={18} />}
    </button>
  );
}

// ── Clustered marker layer ────────────────────────────────────────────────
//
// The marker render path lives in @/components/map/PwaMapMarkers as
// imperative mapboxgl.Marker instances (one React root per marker
// host via createRoot). MapPage feeds it the locations + selection
// + onSelect callback; the layer manages add / remove / unmount
// internally on every camera-settled supercluster recompute.
//
// The dead Google-era ClusteredMarkers function that used to live
// here was deleted in Phase 2 alongside the Google Maps imports —
// the supercluster wiring + dot-presentation logic was ported into
// the new file rather than reused from this one. Keeping the
// pre-existing comment block above so the per-location vs cluster
// rendering rules stay documented next to MapPage's other map-
// specific helpers.

// (ClusteredMarkers body removed in Phase 2 — see PwaMapMarkers.)

/**
 * The location sheet a marker tap opens: the overview (name, address,
 * per-state connector counts) and the stall list with a Start button on
 * every stall, in one step (Warren, 2026-10-23: "trim the fat"). It used
 * to list the chargers read-only and send the driver to the station page
 * to see the same list again with the Start control; Start now opens the
 * pre-session route directly, which keeps every start gate. The stall
 * list is the station page's own component, so the two never drift.
 *
 * The chargers are the map list's embed, the same set the pin counts,
 * refreshed while the sheet is open (MapInner renders the latest copy).
 */
function BottomSheet({
  loc, onClose, onNavigate,
}: {
  loc: LocationWithChargers;
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation('map');
  const status = getDominantStatus(loc);
  const buckets = connectorBuckets(loc);
  const nonEmptyBuckets = CONNECTOR_BUCKETS.filter((b) => buckets[b.key] > 0);
  // B-3 fix: server-derived online count when available.
  const onlineCount = loc.online_chargers ?? loc.chargers.filter((c) => c.is_online).length;
  return (
    <div className={styles.sheet}>
      {/* Favourite + close. The heart came over from the station page,
          which the sheet no longer links to, so saving a location from
          the map still works. */}
      <div className={styles.sheetActions}>
        <FavoriteHeart stationId={loc._id} className={styles.sheetFav} idleColor="var(--color-tx-m)" />
        <button className={styles.sheetClose} onClick={onClose} aria-label={t('sheet.close')}><X size={18} /></button>
      </div>

      {/* Compact text header. The photo header was dropped to shrink the
          sheet so a clear pinchable band of map sits above it; name +
          address render as plain text, counts + stalls + Navigate follow. */}
      <div className={styles.sheetTextHeader}>
        <h2 className={styles.sheetTitle}>{loc.name}</h2>
        <p className={styles.sheetAddr}>
          {loc.address.street}, {loc.address.city}
        </p>
      </div>

      <div className={styles.statusRow}>
        {nonEmptyBuckets.length > 0 ? (
          <span className={styles.statusBadges}>
            {nonEmptyBuckets.map((b) => (
              <span key={b.key} className={styles.statusBadge} style={{ background: `${b.color}20`, color: b.color }}>
                {t(`bucket.${b.key}`)} {buckets[b.key]}
              </span>
            ))}
          </span>
        ) : (
          <span className={styles.statusBadge} style={{ background: `${STATUS_COLOR[status]}20`, color: STATUS_COLOR[status] }}>
            {t(`status.${status}`)}
          </span>
        )}
        <span className={styles.chargerCount}>
          {t('sheet.chargerCount', { count: loc.chargers.length, online: onlineCount })}
        </span>
      </div>

      <div className={styles.stalls}>
        {/* startFrom: Back on the pre-session page returns to the map,
            not to a station page repeating this sheet. */}
        <StallList
          locationId={loc._id}
          chargers={loc.chargers}
          onNavigate={onNavigate}
          emptyText={t('sheet.empty')}
          startFrom="map"
        />
      </div>

      {loc.address.coordinates && (
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${loc.address.coordinates.lat},${loc.address.coordinates.lng}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.navigateBtn}
        >
          <Navigation size={16} /> {t('sheet.navigate')}
        </a>
      )}
    </div>
  );
}
